/**
 * Site photos as slots, not an uploader: each suggested slot says what to photograph and why, with an example
 * drawing (a pictogram, never a real photo), a camera button on phones (`capture="environment"`), upload or drag and
 * drop, a caption, and delete. Photos are optional; the progress reads "3 of 5 photos" (never a check).
 */
import { useMutation } from "@tanstack/react-query";
import { Camera, CheckCircle2, ChevronDown, ChevronRight, ImagePlus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { errorMessage, rawFetch, type S } from "../api/client";
import { Badge, Button, cx, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { PHOTO_SLOTS, photoProgress, type PhotoSlot } from "../lib/permitWorkspace";
import type { Ws } from "./PermitWorkspace";

export function AuthImage({ url, alt, className }: { url: string; alt: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    rawFetch(url)
      .then((r) => r.blob())
      .then((b) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(b);
        setSrc(revoked);
      })
      .catch(() => setSrc(null));
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [url]);
  return src ? <img src={src} alt={alt} className={className} /> : <span className={cx(className, "pw-img-loading")} aria-label={alt} />;
}

/** The example drawing of a slot: a simple line pictogram of what the photo should show. */
function Example({ slot }: { slot: PhotoSlot }) {
  const t = useT();
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const art: Record<string, ReactNode> = {
    main_panel_open: (
      <g {...common}>
        <rect x="18" y="8" width="44" height="56" rx="3" />
        <path d="M62 10 L74 16 L74 62 L62 64" />
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <rect x="24" y={16 + i * 9} width="12" height="5" rx="1" />
            <rect x="44" y={16 + i * 9} width="12" height="5" rx="1" />
          </g>
        ))}
      </g>
    ),
    main_panel_label: (
      <g {...common}>
        <rect x="12" y="16" width="56" height="40" rx="3" />
        <path d="M18 26 H52 M18 34 H60 M18 42 H44" />
        {/* eslint-disable-next-line solaros-i18n/no-hardcoded-text -- an amperage on a pictogram's rating label (number and unit) */}
        <text x="40" y="52" fontSize="8" textAnchor="middle" fill="currentColor" stroke="none">200 A</text>
      </g>
    ),
    meter: (
      <g {...common}>
        <circle cx="40" cy="36" r="22" />
        <rect x="28" y="28" width="24" height="9" rx="1" />
        <path d="M32 46 H48" />
        <text x="40" y="35" fontSize="7" textAnchor="middle" fill="currentColor" stroke="none">0482</text>
      </g>
    ),
    attic: (
      <g {...common}>
        <path d="M6 52 L40 18 L74 52" />
        {[18, 30, 42, 54, 66].map((x) => (
          <path key={x} d={`M${x} 56 V${Math.max(26, 52 - Math.abs(40 - x))}`} />
        ))}
        <path d="M14 62 H66" strokeDasharray="3 3" />
        <text x="40" y="70" fontSize="7" textAnchor="middle" fill="currentColor" stroke="none">{t("permit.photosStep.example.tape")}</text>
      </g>
    ),
    roof_overview: (
      <g {...common}>
        <path d="M8 40 L40 14 L72 40" />
        <rect x="16" y="40" width="48" height="26" />
        <path d="M26 30 L36 22 M34 32 L44 24 M42 34 L52 26" strokeWidth="3" />
        <rect x="34" y="52" width="10" height="14" />
      </g>
    ),
    sub_panel: (
      <g {...common}>
        <rect x="24" y="12" width="32" height="48" rx="3" />
        {[0, 1, 2].map((i) => (
          <rect key={i} x="30" y={20 + i * 11} width="20" height="6" rx="1" />
        ))}
      </g>
    ),
    inverter_location: (
      <g {...common}>
        <rect x="22" y="14" width="36" height="44" rx="4" />
        <circle cx="40" cy="30" r="6" />
        <path d="M30 46 H50" />
      </g>
    ),
    roof_detail: (
      <g {...common}>
        {[0, 1, 2, 3].map((i) => (
          <path key={i} d={`M8 ${20 + i * 12} Q24 ${14 + i * 12} 40 ${20 + i * 12} T72 ${20 + i * 12}`} />
        ))}
      </g>
    ),
    other: (
      <g {...common}>
        <rect x="14" y="18" width="52" height="38" rx="4" />
        <circle cx="40" cy="37" r="10" />
      </g>
    ),
  };
  return (
    <figure className="pw-example" aria-label={t("permit.photosStep.example.caption")}>
      <svg viewBox="0 0 80 72" aria-hidden>
        {art[slot]}
      </svg>
      <figcaption>{t("permit.photosStep.example.caption")}</figcaption>
    </figure>
  );
}

export function Photos({ ws }: { ws: Ws }) {
  const t = useT();
  const site = ws.site;
  const [moreOpen, setMoreOpen] = useState(false);
  // the camera button where there is one (phones, tablets): `capture` opens it directly
  const touch = ws.phone || (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);
  if (!site) return null;
  const progress = photoProgress(t, site.suggested_slots, site.missing_slots);
  const suggested = PHOTO_SLOTS.filter((s) => site.suggested_slots.includes(s.slot));
  const more = PHOTO_SLOTS.filter((s) => !site.suggested_slots.includes(s.slot));
  const moreCount = site.photos.filter((p) => !site.suggested_slots.includes(p.slot)).length;
  return (
    <div className={cx("pw-photos", touch && "touch")} data-anchor="photos">
      <div className="pw-progress">
        <div className="pw-progress-text">
          <b>{progress.label}</b>
          {progress.done === progress.total && <Badge tone="ok">{t("permit.photosStep.complete")}</Badge>}
        </div>
        <div className="pw-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done}>
          {suggested.map((s) => (
            <span key={s.slot} className={site.missing_slots.includes(s.slot) ? "" : "on"} />
          ))}
        </div>
      </div>
      <div className="pw-slots">
        {suggested.map((s) => (
          <Slot key={s.slot} ws={ws} slot={s.slot} title={t(s.title)} why={t(s.why)} photos={site.photos.filter((p) => p.slot === s.slot)} />
        ))}
      </div>
      <button className="pw-more" onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen}>
        {moreOpen ? <ChevronDown size={15} /> : <DirIcon><ChevronRight size={15} /></DirIcon>} {t("permit.photosStep.more", { count: moreCount })}
      </button>
      {moreOpen && (
        <div className="pw-slots">
          {more.map((s) => (
            <Slot key={s.slot} ws={ws} slot={s.slot} title={t(s.title)} why={t(s.why)} photos={site.photos.filter((p) => p.slot === s.slot)} />
          ))}
        </div>
      )}
      <p className="small muted" style={{ margin: 0 }}>
        {t("permit.photosStep.formats")}
      </p>
    </div>
  );
}

function Slot({ ws, slot, title, why, photos }: { ws: Ws; slot: PhotoSlot; title: string; why: string; photos: S["PermitPhoto"][] }) {
  const t = useT();
  const toast = useToast();
  const [caption, setCaption] = useState("");
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        form.append("slot", slot);
        if (caption.trim()) form.append("caption", caption.trim().slice(0, 200));
        await rawFetch(`/api/v1/designs/${ws.designId}/permit-site/photos`, { method: "POST", body: form });
      }
    },
    onSuccess: () => {
      setCaption("");
      ws.refreshSite();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const remove = useMutation({
    mutationFn: (id: string) => rawFetch(`/api/v1/designs/${ws.designId}/permit-site/photos/${id}`, { method: "DELETE" }),
    onSuccess: () => ws.refreshSite(),
    onError: (e) => toast(errorMessage(e), true),
  });
  const pick = (list: FileList | null) => {
    const files = Array.from(list ?? []).filter((f) => /^image\//.test(f.type));
    if (files.length) upload.mutate(files);
  };
  const done = photos.length > 0;
  return (
    <div
      className={cx("pw-slot", done && "done", over && "over")}
      onDragOver={(e) => {
        if (!ws.canEdit) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (ws.canEdit) pick(e.dataTransfer.files);
      }}
    >
      <Example slot={slot} />
      <div className="pw-slot-body">
        <div className="pw-slot-title">
          {done ? <CheckCircle2 size={16} className="pw-ic ok" /> : <span className="pw-slot-dot" />}
          <b>{title}</b>
        </div>
        <p className="small muted">{why}</p>
        {photos.length > 0 && (
          <div className="pw-thumbs">
            {photos.map((p) => (
              <figure key={p.id} className="pw-thumb">
                <AuthImage url={p.url} alt={p.caption || title} />
                <figcaption className="ellipsis" title={p.caption ?? undefined}>{p.caption || <span className="faint">{t("permit.photosStep.noCaption")}</span>}</figcaption>
                {ws.canEdit && (
                  <button className="pw-thumb-del" onClick={() => remove.mutate(p.id)} aria-label={t("permit.photosStep.deletePhoto")} title={t("permit.photosStep.deletePhoto")}>
                    <Trash2 size={13} />
                  </button>
                )}
              </figure>
            ))}
          </div>
        )}
        {ws.canEdit && (
          <div className="pw-slot-actions">
            <input className="input pw-caption" placeholder={t("permit.photosStep.captionPlaceholder")} value={caption} maxLength={200} onChange={(e) => setCaption(e.target.value)} aria-label={t("permit.photosStep.captionFor", { title })} />
            <Button size="sm" variant={done ? "default" : "primary"} className="pw-camera" icon={<Camera size={14} />} loading={upload.isPending} onClick={() => cameraRef.current?.click()}>
              {t("permit.photosStep.takePhoto")}
            </Button>
            <Button size="sm" icon={<ImagePlus size={14} />} disabled={upload.isPending} onClick={() => fileRef.current?.click()}>
              {t("permit.photosStep.upload")}
            </Button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
          </div>
        )}
      </div>
    </div>
  );
}
